/**
 * The trainer's identity, typed: their Pokémon GO trainer name and level.
 *
 * HISTORY
 *   Until 2026-10-02 both were read off a screenshot of the trainer's Pokémon
 *   GO profile. Any picture was accepted as one (a photo of a desk went
 *   through, and the form was then filled in by hand anyway), and what the read
 *   returned was wrong often enough to matter: accounts were saved as
 *   "SCOPELY" (a badge), as "MOI" (a tab), and under their buddy's name.
 *
 * NOW (owner direction, 2026-10-02)
 *   No screenshot anywhere. Two typed fields at sign-up, the same two in
 *   Settings to change them. "Profile set" means the account has a trainer
 *   name; nothing else gates anything.
 *
 * Pure, so the form, the gates and the tests share one rule.
 */

/** Pokémon GO trainer names are letters and digits, at most 15 of them. */
export const TRAINER_NAME_MAX = 15;
/** Shortest name the form accepts. */
export const TRAINER_NAME_MIN = 3;

/**
 * What the name field holds after a keystroke or a paste: letters and digits
 * only, capped at the game's length. Spaces, punctuation and emoji are dropped
 * as they are typed, so "Ash Ketchum 99" pasted from a chat becomes
 * "AshKetchum99" in front of the trainer rather than failing on submit.
 */
export function sanitizeTrainerNameInput(raw: string | null | undefined): string {
  return (raw ?? "").replace(/[^A-Za-z0-9]/g, "").slice(0, TRAINER_NAME_MAX);
}

export type TrainerNameValidation = { ok: true; value: string } | { ok: false; reason: string };

/** Is this a name the form can send? The reason is user-facing copy. */
export function validateTrainerName(raw: string | null | undefined): TrainerNameValidation {
  const value = sanitizeTrainerNameInput(raw);
  if (value.length === 0) return { ok: false, reason: "Enter your trainer name" };
  if (value.length < TRAINER_NAME_MIN) {
    return { ok: false, reason: `Trainer names have at least ${TRAINER_NAME_MIN} letters or numbers` };
  }
  return { ok: true, value };
}

/**
 * Has this account told us who its trainer is?
 *
 * THE profile-complete rule, for every gate (ProfileGate, Live Scan, the web
 * portal). It used to be "has a profile screenshot", which was true of an
 * account that uploaded a picture and never confirmed a name, and false of
 * nobody the app could actually name.
 */
export function isTrainerProfileSet(
  account: { trainerName?: string | null } | null | undefined,
): boolean {
  return (account?.trainerName ?? "").trim().length > 0;
}

/**
 * The day the typed step replaced the profile screenshot. An account older
 * than this once went through (or was asked for) the screenshot.
 */
export const TYPED_STEP_SINCE_MS = Date.UTC(2026, 9, 2);

type StepAccount = {
  trainerName?: string | null;
  trainerConfirmed?: boolean;
  createdAt?: string | number | null;
};

/**
 * Has this account still to TYPE its trainer name and level?
 *
 * Owner decision, 2026-10-02: the profile screenshots and everything read off
 * them were removed, and every existing account is asked, the next time it
 * opens Nexus Dex, to type its name and level. The server keeps the answer on
 * the account (`trainerConfirmed`), so a trainer is asked once, not once per
 * phone.
 *
 * Strictly `false`: a payload with no such field (a cached account from an
 * older build of the app, or a server that has not deployed the flag yet) is
 * "not known", and nobody is interrupted on a guess.
 */
export function needsTrainerConfirm(account: StepAccount | null | undefined): boolean {
  return account != null && account.trainerConfirmed === false;
}

/**
 * Which face the trainer step shows, or null when it stays shut.
 *
 *   "setup"    a new trainer: name and level, for the first time.
 *   "confirm"  a trainer from before the typed step: the same two fields,
 *              prefilled with what the account holds, and a line saying why
 *              they are being asked.
 *
 * It opens when it has been ASKED FOR on this device (sign-up, Live Scan's
 * "Add my trainer name": lib/profileSetupRequest), or when the account has
 * never typed its identity and has not put the question off since the app was
 * opened ("Not now" lasts until the next launch).
 */
export function trainerStepKind(
  account: StepAccount | null | undefined,
  state: { requested: boolean; deferred: boolean },
): "setup" | "confirm" | null {
  if (!account) return null;
  const named = isTrainerProfileSet(account);
  const owed = needsTrainerConfirm(account) && !state.deferred;
  if (!named && state.requested) return "setup";
  if (!owed) return null;
  if (named) return "confirm";
  const created =
    typeof account.createdAt === "string" ? Date.parse(account.createdAt) : Number(account.createdAt);
  return Number.isFinite(created) && created < TYPED_STEP_SINCE_MS ? "confirm" : "setup";
}
